import React, { useState, useEffect } from "react";
import { IClinicInfo, IOpeningHour } from "../types/clinic.types";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import bookUrl from "../../../../api/bookUrl";
import Select from "react-select";
import jsonData from "../../../utils/location/cities-and-postalcode-by-district.json";
import { toast } from "react-toastify";
// --- TYPE DEFINITIONS ---

// Shape of a single city in your JSON
type City = {
  city: string;
  code: string;
};

// Shape of the entire JSON data
type CityDataType = Record<string, City[]>;

// A. Option type for the FIRST select (Districts)
type DistrictOption = {
  value: string; // "Ratnapura", "Colombo", etc.
  label: string;
};

// B. Option type for the SECOND select (Cities)
type CityOption = {
  value: string; // "Akarella", "Athurugiriya", etc. (the city name)
  label: string; // "Akarella", "Athurugiriya", etc. (the city name)
};

// --- DATA PREPARATION ---

// Cast the imported JSON to our defined type
const cityData: CityDataType = jsonData;

// Get the district names from the keys
const districtKeys = Object.keys(cityData);

// Format the district keys for the first react-select
const districtOptions: DistrictOption[] = districtKeys.map((key) => ({
  value: key,
  label: key,
}));

interface Props {
  info: IClinicInfo | null;
  onInfoChange: (info: IClinicInfo) => void;
}

export const ClinicInfo: React.FC<Props> = ({ info, onInfoChange }) => {
  
  const [cityOptions, setCityOptions] = useState<CityOption[]>([]);
  const [selectedCity, setSelectedCity] = useState<CityOption | null>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<DistrictOption | null>(null);

  // Default days of the week
  const defaultDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  // Initialize opening hours with default days if info doesn't have any
  const getInitialOpeningHours = (): IOpeningHour[] => {
    if (info?.openingHours && info.openingHours.length > 0) {
      return info.openingHours;
    }
    return defaultDays.map((day) => ({
      day,
      openTime: "",
      closeTime: "",
      isClosed: day === "Sunday", // Sunday closed by default
    }));
  };

  const [localInfo, setLocalInfo] = useState<IClinicInfo>({
    ...(info || {
      name: "",
      slug: "",
      description: "",
      email: "",
      openingHours: [],
    }),
    openingHours: getInitialOpeningHours(),
    location: (info?.location || {
      addressLine: "",
      street: "",
      district: "",
      city: "",
      map: "",
    }),
  });

  // Helper function to find city by name
  const findCity = (cityName: string, districtName?: string): City | null => {
    if (districtName && cityData[districtName]) {
      const city = cityData[districtName].find((c: City) => c.city === cityName);
      if (city) return city;
    }
    // If district not found or not provided, search all districts
    for (const district in cityData) {
      const city = cityData[district].find((c: City) => c.city === cityName);
      if (city) return city;
    }
    return null;
  };

  // Sync localInfo with info prop changes
  useEffect(() => {
    if (info) {
      const openingHours = info.openingHours && info.openingHours.length > 0 
        ? info.openingHours 
        : defaultDays.map((day) => ({
            day,
            openTime: "",
            closeTime: "",
            isClosed: day === "Sunday",
          }));
      
      setLocalInfo({
        ...info,
        openingHours,
        location: info.location || {
          addressLine: "",
          street: "",
          district: "",
          city: "",
          map: "",
        },
      });
    }
  }, [info]);

  // Initialize district and city from API data
  useEffect(() => {
    // Get district and city from location object
    const district = info?.location?.district;
    const cityValue = info?.location?.city;

    if (district) {
      const districtOption: DistrictOption = {
        value: district,
        label: district,
      };
      setSelectedDistrict(districtOption);

      // Load city options for this district
      const rawCities = cityData[district] || [];
      const formattedCityOptions = rawCities.map((city: City) => ({
        value: city.city,
        label: city.city,
      }));
      setCityOptions(formattedCityOptions);

      // If city value exists, find and set the city
      if (cityValue) {
        const foundCity = findCity(cityValue, district);
        if (foundCity) {
          setSelectedCity({
            value: foundCity.city,
            label: foundCity.city,
          });
        }
      }
    } else if (cityValue) {
      // If only city name exists without district, find both
      const foundCity = findCity(cityValue);
      if (foundCity) {
        // Find which district this city belongs to
        for (const district in cityData) {
          const city = cityData[district].find((c: City) => c.city === cityValue);
          if (city) {
            const districtOption: DistrictOption = {
              value: district,
              label: district,
            };
            setSelectedDistrict(districtOption);

            // Load city options for this district
            const rawCities = cityData[district] || [];
            const formattedCityOptions = rawCities.map((c: City) => ({
              value: c.city,
              label: c.city,
            }));
            setCityOptions(formattedCityOptions);

            setSelectedCity({
              value: foundCity.city,
              label: foundCity.city,
            });
            break;
          }
        }
      }
    }
  }, [info?.location?.district, info?.location?.city]);
  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (e.target.name === "name") {
      setLocalInfo({
        ...localInfo,
        [e.target.name]: e.target.value,
        slug: generateSlug(e.target.value),
      });
    } else if (["street", "addressLine", "map"].includes(e.target.name)) {
      // Handle location fields
      setLocalInfo({
        ...localInfo,
        location: {
          ...localInfo.location,
          [e.target.name]: e.target.value,
        },
      });
    } else {
      setLocalInfo({ ...localInfo, [e.target.name]: e.target.value });
    }
  };

  const handleHourChange = (
    index: number,
    field: keyof IOpeningHour,
    value: string | boolean
  ) => {
    const newHours = [...localInfo.openingHours];
    (newHours[index] as any)[field] = value;
    setLocalInfo({ ...localInfo, openingHours: newHours });
  };

  const handleSave = () => {
    const updatedInfo = {
      ...localInfo,
      location: {
        ...localInfo.location,
        district: selectedDistrict?.value || "",
        city: selectedCity?.value || "",
      },
    };
    onInfoChange(updatedInfo);
    
    bookUrl
      .patch("/bookingclinic/clinic/addBookClinicInfo", updatedInfo)
      .then((response) => {
        if (response.data?.success) {
          toast.success("Clinic information saved successfully!");
        } else {
          toast.error("Failed to save clinic information");
        }
      })
      .catch((error) => {
        console.error(error);
        toast.error(error?.response?.data?.message || "Failed to save clinic information");
      });
  };

  const handleDistrictChange = (selectedOption: DistrictOption | null) => {
    setSelectedDistrict(selectedOption);
    setSelectedCity(null);
    setCityOptions([]);
    if (selectedOption) {
      const rawCities = cityData[selectedOption.value] || [];
      const formattedCityOptions = rawCities.map((city: City) => ({
        value: city.city,
        label: city.city,
      }));
      setCityOptions(formattedCityOptions);
      
      // Update localInfo location
      setLocalInfo({
        ...localInfo,
        location: {
          ...localInfo.location,
          district: selectedOption.value,
          city: "", // Clear city when district changes
        },
      });
    } else {
      // Clear district when deselected
      setLocalInfo({
        ...localInfo,
        location: {
          ...localInfo.location,
          district: "",
          city: "",
        },
      });
    }
  };

  const handleCityChange = (selectedOption: CityOption | null) => {
    setSelectedCity(selectedOption);
    
    // Update localInfo location
    setLocalInfo({
      ...localInfo,
      location: {
        ...localInfo.location,
        city: selectedOption?.value || "",
      },
    });
  };
  return (
    <section className="bg-white rounded-[8px] shadow-lg shadow-slate-200/50 border border-slate-100 p-3">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5 pb-2.5 mb-3 border-b border-slate-200">
        <h2 className="text-xs font-bold text-primary mb-0">Basic Information</h2>
        <button 
          className="flex items-center gap-1.5 px-4 py-2.5 bg-gradient-to-r from-primary to-primaryLight text-white rounded-lg font-medium hover:shadow-lg hover:shadow-primary/20 transition-all text-xs"
          onClick={handleSave}
        >
          <SaveAltIcon sx={{ fontSize: 16 }} />
          Save
        </button>
      </div>

      <form className="space-y-3">
        <div>
          <label htmlFor="name" className="block text-xs font-semibold text-slate-700 mb-1">Clinic Name</label>
          <input
            type="text"
            id="name"
            name="name"
            value={localInfo.name}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-xs text-slate-700"
          />
        </div>
        <div>
          <label htmlFor="slug" className="block text-xs font-semibold text-slate-700 mb-1">Slug</label>
          <input
            type="text"
            id="slug"
            name="slug"
            value={localInfo.slug}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-xs text-slate-700"
          />
        </div>
        <div>
          <label htmlFor="description" className="block text-xs font-semibold text-slate-700 mb-1">Description</label>
          <textarea
            id="description"
            name="description"
            value={localInfo.description}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-y min-h-[80px] text-xs text-slate-700"
          />
        </div>

        <div>
          <label htmlFor="district-select" className="block text-xs font-semibold text-slate-700 mb-1">Choose a District:</label>
          <Select
            id="district-select"
            options={districtOptions}
            value={selectedDistrict}
            onChange={handleDistrictChange}
            isSearchable
            isClearable
            placeholder="Type to search districts..."
            className="react-select-container"
            classNamePrefix="react-select"
          />
        </div>
        <div>
          <label htmlFor="city-select" className="block text-xs font-semibold text-slate-700 mb-1">Choose a City:</label>
          <Select
            id="city-select"
            options={cityOptions}
            value={selectedCity}
            onChange={handleCityChange}
            isSearchable
            isClearable
            isDisabled={!selectedDistrict}
            placeholder={
              selectedDistrict
                ? "Type to search cities..."
                : "-- Choose a district first --"
            }
            className="react-select-container"
            classNamePrefix="react-select"
          />
        </div>
        <div>
          <label htmlFor="street" className="block text-xs font-semibold text-slate-700 mb-1">Street</label>
          <input
            type="text"
            id="street"
            name="street"
            value={localInfo.location.street}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-xs text-slate-700"
          />
        </div>
        <div>
          <label htmlFor="addressLine" className="block text-xs font-semibold text-slate-700 mb-1">Address Line</label>
          <input
            type="text"
            id="addressLine"
            name="addressLine"
            value={localInfo.location.addressLine}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-xs text-slate-700"
          />
        </div>
        <div>
          <label htmlFor="map" className="block text-xs font-semibold text-slate-700 mb-1">Embed Map</label>
          <input
            type="text"
            id="map"
            name="map"
            value={localInfo.location.map}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-xs text-slate-700"
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-xs font-semibold text-slate-700 mb-1">Email</label>
          <input
            type="email"
            id="email"
            name="email"
            value={localInfo.email}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-xs text-slate-700"
          />
        </div>
      </form>

      <div className="mt-3 border-t border-slate-200 pt-3">
        <div className="flex items-center justify-between mb-2.5">
          <h3 className="text-sm font-semibold text-slate-800">Opening Hours</h3>
          <button
            type="button"
            onClick={() => {
              const newDay: IOpeningHour = {
                day: "",
                openTime: "",
                closeTime: "",
                isClosed: false,
              };
              setLocalInfo({
                ...localInfo,
                openingHours: [...localInfo.openingHours, newDay],
              });
            }}
            className="px-3 py-1.5 text-xs font-medium text-primary border border-primary rounded-lg hover:bg-primary hover:text-white transition-colors"
          >
            + Add Day
          </button>
        </div>
        <div className="flex flex-col gap-2.5">
          {localInfo.openingHours.length === 0 ? (
            <p className="text-xs text-slate-500 italic">No opening hours added. Click "Add Day" to add one.</p>
          ) : (
            localInfo.openingHours.map((day, index) => (
              <div
                key={`${day.day}-${index}`}
                className={`grid gap-2.5 items-center p-2.5 border border-slate-100 rounded-lg ${
                  day.isClosed ? "grid-cols-[100px_1fr_auto_auto]" : "grid-cols-[100px_1fr_1fr_auto_auto]"
                }`}
              >
                <input
                  type="text"
                  value={day.day}
                  placeholder="Day name"
                  onChange={(e) =>
                    handleHourChange(index, "day", e.target.value)
                  }
                  className="px-2.5 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-xs text-slate-700 font-semibold"
                />

                {!day.isClosed ? (
                  <div className="flex gap-1.5">
                    <input
                      type="time"
                      value={day.openTime}
                      className="px-2.5 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-xs text-slate-700 w-[120px] bg-white"
                      onChange={(e) =>
                        handleHourChange(index, "openTime", e.target.value)
                      }
                    />
                    <input
                      type="time"
                      value={day.closeTime}
                      className="px-2.5 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-xs text-slate-700 w-[120px] bg-white"
                      onChange={(e) =>
                        handleHourChange(index, "closeTime", e.target.value)
                      }
                    />
                  </div>
                ) : (
                  <span className="text-xs text-slate-500 italic">Closed</span>
                )}

                <div className="flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    id={`closed-${index}`}
                    checked={day.isClosed}
                    onChange={(e) =>
                      handleHourChange(index, "isClosed", e.target.checked)
                    }
                    className="w-3.5 h-3.5 text-primary border-slate-300 rounded focus:ring-primary"
                  />
                  <label htmlFor={`closed-${index}`} className="text-xs font-normal text-slate-700">Closed</label>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    const newHours = localInfo.openingHours.filter((_, i) => i !== index);
                    setLocalInfo({ ...localInfo, openingHours: newHours });
                  }}
                  className="px-2 py-1 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                  title="Remove this day"
                >
                  ×
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  );
};
